import { Loader2, Plus, Trash2, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { AvatarUpload } from '@/components/prompt-sets/AvatarUpload';
import { cn } from '@/lib/utils';

import type { PromptItem, PromptSet, PromptSetFormData } from '@/types/promptSet.types';

interface PromptSetFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: PromptSetFormData) => Promise<void>;
  mode: 'create' | 'edit';
  promptSet?: PromptSet;
  promptSetId?: string;
}

const RETAKE_POLICY_OPTIONS = ['unlimited', 'once', 'none'] as const;

const promptSchema = z.object({
  name: z.string().min(1),
  retakePolicy: z.enum(['unlimited', 'once', 'none']),
  maxDurationSec: z.number().min(1),
  active: z.boolean(),
  prompts: z
    .array(
      z.object({
        promptId: z.string(),
        text: z.string().min(1),
        maxDurationSec: z.number().min(1),
        order: z.number(),
        videoUrl: z.string().optional(),
        videoDurationSec: z.number().optional(),
      }),
    )
    .min(1),
});

const labelClassName = 'block text-sm font-medium text-gray-700';
const inputClassName =
  'mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500';

function createEmptyPrompt(order: number): PromptItem {
  return {
    promptId: crypto.randomUUID(),
    text: '',
    maxDurationSec: 120,
    order,
  };
}

function buildInitialFormData(promptSet?: PromptSet): PromptSetFormData {
  if (!promptSet) {
    return {
      name: '',
      retakePolicy: 'unlimited',
      maxDurationSec: 120,
      active: false,
      prompts: [createEmptyPrompt(1)],
    };
  }

  return {
    name: promptSet.name,
    retakePolicy: promptSet.retakePolicy,
    maxDurationSec: promptSet.maxDurationSec,
    active: promptSet.active,
    prompts: promptSet.prompts.map((p, i) => ({
      promptId: p.promptId,
      text: p.text,
      maxDurationSec: p.maxDurationSec,
      order: i + 1,
      videoUrl: p.videoUrl,
      videoDurationSec: p.videoDurationSec,
    })),
  };
}

export function PromptSetFormModal({ isOpen, onClose, onSave, mode, promptSet, promptSetId }: PromptSetFormModalProps) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<PromptSetFormData>(() => buildInitialFormData(promptSet));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setFormData(buildInitialFormData(mode === 'edit' ? promptSet : undefined));
      setErrors({});
    }
  }, [isOpen, mode, promptSet]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setErrors({});

      const result = promptSchema.safeParse(formData);
      if (!result.success) {
        const fieldErrors: Record<string, string> = {};
        for (const issue of result.error.issues) {
          const path = issue.path.join('.');
          fieldErrors[path] = t(`promptSets.form.validation.${path}`, issue.message);
        }
        setErrors(fieldErrors);
      } else {
        setIsSubmitting(true);
        try {
          await onSave(result.data);
        } finally {
          setIsSubmitting(false);
        }
      }
    },
    [formData, onSave, t],
  );

  const addQuestion = useCallback(() => {
    setFormData((prev) => ({
      ...prev,
      prompts: [...prev.prompts, createEmptyPrompt(prev.prompts.length + 1)],
    }));
  }, []);

  const removeQuestion = useCallback((index: number) => {
    setFormData((prev) => ({
      ...prev,
      prompts: prev.prompts.filter((_, i) => i !== index).map((p, i) => ({ ...p, order: i + 1 })),
    }));
  }, []);

  const updateQuestion = useCallback((index: number, field: keyof PromptItem, value: string | number) => {
    setFormData((prev) => ({
      ...prev,
      prompts: prev.prompts.map((p, i) => (i === index ? { ...p, [field]: value } : p)),
    }));
  }, []);

  if (!isOpen) return null;

  const titleKey = mode === 'create' ? 'promptSets.form.titleCreate' : 'promptSets.form.titleEdit';

  return (
    <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/50'>
      <div className='mx-4 flex max-h-[90vh] w-full max-w-2xl flex-col rounded-xl bg-white shadow-xl'>
        <div className='flex items-center justify-between border-b border-gray-200 px-6 py-4'>
          <h2 className='text-lg font-semibold text-gray-900'>{t(titleKey)}</h2>
          <button onClick={onClose} className='rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600'>
            <X className='h-5 w-5' />
          </button>
        </div>

        <form onSubmit={(e) => void handleSubmit(e)} className='flex-1 space-y-6 overflow-y-auto px-6 py-4'>
          {/* Name */}
          <div>
            <label className={labelClassName}>{t('promptSets.form.name')}</label>
            <input
              type='text'
              value={formData.name}
              onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
              className={cn(inputClassName, errors['name'] && 'border-red-500')}
              placeholder={t('promptSets.form.namePlaceholder')}
            />
            {errors['name'] && <p className='mt-1 text-xs text-red-600'>{errors['name']}</p>}
          </div>

          {/* Retake Policy */}
          <div>
            <label className={labelClassName}>{t('promptSets.form.retakePolicy')}</label>
            <select
              value={formData.retakePolicy}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  retakePolicy: e.target.value as PromptSetFormData['retakePolicy'],
                }))
              }
              className={inputClassName}>
              {RETAKE_POLICY_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {t(`promptSets.retakePolicies.${option}`)}
                </option>
              ))}
            </select>
          </div>

          {/* Max Duration per Question */}
          <div>
            <label className={labelClassName}>{t('promptSets.form.maxDurationSec')}</label>
            <input
              type='number'
              min={1}
              value={formData.maxDurationSec}
              onChange={(e) => setFormData((prev) => ({ ...prev, maxDurationSec: Number(e.target.value) }))}
              className={cn(inputClassName, errors['maxDurationSec'] && 'border-red-500')}
            />
            {errors['maxDurationSec'] && <p className='mt-1 text-xs text-red-600'>{errors['maxDurationSec']}</p>}
          </div>

          {/* Active */}
          <div>
            <label className='flex items-center gap-3'>
              <input
                type='checkbox'
                checked={formData.active}
                onChange={(e) => setFormData((prev) => ({ ...prev, active: e.target.checked }))}
                className='rounded border-gray-300 text-blue-600 focus:ring-blue-500'
              />
              <span className='text-sm font-medium text-gray-700'>{t('promptSets.form.active')}</span>
            </label>
            <p className='ml-7 mt-1 text-xs text-gray-500'>{t('promptSets.form.activeHelp')}</p>
          </div>

          {/* Questions */}
          <div>
            <div className='mb-3 flex items-center justify-between'>
              <label className={labelClassName}>{t('promptSets.form.questions')}</label>
              <button
                type='button'
                onClick={addQuestion}
                className='inline-flex items-center gap-1 rounded-md bg-blue-50 px-3 py-1.5 text-xs font-medium text-blue-700 hover:bg-blue-100'>
                <Plus className='h-3.5 w-3.5' />
                {t('promptSets.form.addQuestion')}
              </button>
            </div>

            {errors['prompts'] && <p className='mb-2 text-xs text-red-600'>{errors['prompts']}</p>}

            <div className='space-y-4'>
              {formData.prompts.map((prompt, index) => (
                <div key={prompt.promptId} className='rounded-lg border border-gray-200 bg-gray-50 p-4'>
                  <div className='mb-3 flex items-center justify-between'>
                    <span className='text-sm font-medium text-gray-600'>
                      {t('promptSets.form.questionNumber', { number: index + 1 })}
                    </span>
                    <button
                      type='button'
                      onClick={() => removeQuestion(index)}
                      disabled={formData.prompts.length <= 1}
                      className={cn(
                        'inline-flex items-center gap-1 rounded p-1 text-xs',
                        formData.prompts.length <= 1
                          ? 'cursor-not-allowed text-gray-300'
                          : 'text-red-500 hover:bg-red-50 hover:text-red-700',
                      )}>
                      <Trash2 className='h-3.5 w-3.5' />
                      {t('promptSets.form.removeQuestion')}
                    </button>
                  </div>

                  <div className='space-y-3'>
                    <div>
                      <label className='block text-xs font-medium text-gray-600'>
                        {t('promptSets.form.questionText')}
                      </label>
                      <textarea
                        value={prompt.text}
                        onChange={(e) => updateQuestion(index, 'text', e.target.value)}
                        rows={2}
                        className={cn(
                          'mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500',
                          errors[`prompts.${index}.text`] && 'border-red-500',
                        )}
                        placeholder={t('promptSets.form.questionTextPlaceholder')}
                      />
                      {errors[`prompts.${index}.text`] && (
                        <p className='mt-1 text-xs text-red-600'>{errors[`prompts.${index}.text`]}</p>
                      )}
                    </div>

                    <div>
                      <label className='block text-xs font-medium text-gray-600'>
                        {t('promptSets.form.questionMaxDuration')}
                      </label>
                      <input
                        type='number'
                        min={1}
                        value={prompt.maxDurationSec}
                        onChange={(e) => updateQuestion(index, 'maxDurationSec', Number(e.target.value))}
                        className='mt-1 block w-32 rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500'
                      />
                    </div>

                    {/* Avatar Video Upload - only in edit mode */}
                    {mode === 'edit' && promptSetId ? (
                      <AvatarUpload
                        promptSetId={promptSetId}
                        promptIndex={index}
                        hasVideo={!!prompt.videoUrl}
                        videoDurationSec={prompt.videoDurationSec}
                        onUploaded={(videoUrl, durationSec) => {
                          setFormData((prev) => ({
                            ...prev,
                            prompts: prev.prompts.map((p, i) =>
                              i === index ? { ...p, videoUrl, videoDurationSec: durationSec } : p,
                            ),
                          }));
                        }}
                        onRemoved={() => {
                          setFormData((prev) => ({
                            ...prev,
                            prompts: prev.prompts.map((p, i) =>
                              i === index ? { ...p, videoUrl: undefined, videoDurationSec: undefined } : p,
                            ),
                          }));
                        }}
                      />
                    ) : null}
                    {mode === 'create' ? (
                      <p className='mt-2 text-xs text-gray-400 italic'>{t('promptSets.form.uploadVideoHint')}</p>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </form>

        <div className='flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4'>
          <button
            type='button'
            onClick={onClose}
            disabled={isSubmitting}
            className='rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50'>
            {t('promptSets.form.cancel')}
          </button>
          <button
            type='submit'
            disabled={isSubmitting}
            onClick={(e) => void handleSubmit(e as unknown as React.FormEvent)}
            className='inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50'>
            {isSubmitting && <Loader2 className='h-4 w-4 animate-spin' />}
            {isSubmitting ? t('promptSets.form.saving') : t('promptSets.form.save')}
          </button>
        </div>
      </div>
    </div>
  );
}
